import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import ContractorPicker from '../../components/ContractorPicker';
import { useAlert } from '../../context/AlertContext';
import { usePermissions } from '../../context/PermissionContext';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { nowInTenantInput, addHoursInTenantInput, tenantInputToInstant,
         toTenantLocalInput, toWallClockInput } from '../../utils/tenantTime';
import { formatDate } from '../../utils/dateUtils';
import FileUploadWidget from '../../components/FileUploadWidget';

const TrainingRecords = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const dropdownRef = useRef(null);
  const highlightedRecordRef = useRef(null);
  // Whether the deep-linked record has already been located. A ref, not
  // state, so setting it cannot retrigger the fetch it guards.
  const locatedRef = useRef(false);
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const { hasPermission, isAdmin } = usePermissions();
  const { activeSiteId, activeSiteName } = useSite();
  const [records, setRecords] = useState([]);
  const [courses, setCourses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  // Two pieces of state on purpose: `searchInput` follows every keystroke so
  // the box stays responsive, `searchTerm` lags behind it and is what the
  // server query depends on — otherwise every character would refetch.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  // The contractor chosen in the trainee picker. Held HERE because
  // ContractorPicker is controlled: passing a literal '' meant every selection
  // snapped straight back to "None", so the contractor had to be re-picked for
  // each worker added. The worker stays uncontrolled ('') on purpose — it
  // clears after each add, which is what makes adding a second worker from the
  // same contractor one click instead of three.
  const [traineeContractorId, setTraineeContractorId] = useState('');

  // Multi-select buffer used in create mode. Edit mode still operates on
  // a single record (you can only edit one row at a time), so it keeps
  // using scheduleForm.employee_id/employee_name like before.
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(null); // 'employee_name' or 'course_title'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // Server-side pagination. Filtering and sorting are sent to the server with
  // the page, because applying either to the rows already returned would only
  // filter or sort the CURRENT page — which reads as lost rows and a broken
  // sort the moment there is more than one page.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const [scheduleForm, setScheduleForm] = useState({
    employee_id: '',
    employee_name: '',
    course_id: '',
    course_title: '',
    training_date: new Date().toISOString().split('T')[0],
    training_start: '',
    training_end: '',
    attendees: [],
    completion_status: 'Scheduled',
    trainer_name: '',
    duration_hours: '',
    notes: ''
  });

  const [completeForm, setCompleteForm] = useState({
    quiz_score: '',
    trainer_evaluation: '',
    manager_evaluation: '',
    certificate_issued: false,
    certificate_number: '',
    attendance_verified: false
  });

  // File upload state for Complete Training modal
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [initialAttachmentIds, setInitialAttachmentIds] = useState([]);

  // Track the passing score and quiz availability for the selected course
  const [coursePassingScore, setCoursePassingScore] = useState(null);
  const [courseHasQuiz, setCourseHasQuiz] = useState(false);
  const [courseEvidenceRequired, setCourseEvidenceRequired] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeSiteId]);

  // A filter or sort change re-cuts the whole result set, so page 3 of the old
  // list is meaningless — go back to the first page rather than showing an
  // empty one.
  useEffect(() => {
    setPage(1);
  }, [filterStatus, sortColumn, sortDirection, pageSize, activeSiteId]);

  // Scroll to highlighted record when data loads
  useEffect(() => {
    if (highlightId && records.length > 0 && highlightedRecordRef.current) {
      setTimeout(() => {
        highlightedRecordRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [highlightId, records]);

  // The status filter and the sort are applied by the SERVER (see
  // fetchRecords), so the rows arrive already filtered and ordered. Re-doing
  // either here would act on one page only: "Completed" would hide completed
  // rows on other pages, and the sort would order 25 rows out of 91.
  useEffect(() => {
    setFilteredRecords(records);
  }, [records]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowEmployeeDropdown(false);
      }
    };

    if (showEmployeeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmployeeDropdown]);

  useEffect(() => {
    if (employeeSearch.trim() === '') {
      setFilteredEmployees(employees);
    } else {
      const search = employeeSearch.toLowerCase();
      setFilteredEmployees(
        employees.filter(emp => 
          (emp.name && emp.name.toLowerCase().includes(search)) ||
          (emp.email && emp.email.toLowerCase().includes(search)) ||
          (emp.id && emp.id.toLowerCase().includes(search))
        )
      );
    }
  }, [employeeSearch, employees]);

  // One page of records. Separate from fetchData so turning a page does not
  // also refetch the course catalogue and the entire user list.
  // Debounce the typing, and go back to page 1: staying on page 3 of the old
  // result set shows an empty page and reads as "search is broken".
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchTerm(prev => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Export what is CURRENTLY filtered, not everything: handing someone a file
  // that does not match the list they were looking at is worse than no export.
  const [exporting, setExporting] = useState(null);
  const exportRecords = async (format) => {
    setExporting(format);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus !== 'All') params.set('status', filterStatus);
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (sortColumn) { params.set('sort', sortColumn); params.set('order', sortDirection); }
      const res = await axios.get(
        `${API}/training-records/export.${format}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `training_records_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError(`Could not export the ${format === 'xlsx' ? 'spreadsheet' : 'PDF'}.`);
    } finally {
      setExporting(null);
    }
  };

  const fetchRecords = useCallback(async (targetPage = page) => {
    setRecordsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(targetPage),
        page_size: String(pageSize),
      });
      if (filterStatus !== 'All') params.set('status', filterStatus);
      // Searched in SQL, not in the page: the list is paginated, so filtering
      // the fetched rows would only ever search what is already on screen.
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (sortColumn) {
        params.set('sort', sortColumn);
        params.set('order', sortDirection);
      }
      // Deep link from a notification: ask the server for the page that holds
      // the record rather than hoping it falls on this one. Only on the first
      // load — afterwards the user's own paging must win.
      if (highlightId && !locatedRef.current) params.set('locate', highlightId);
      const res = await axios.get(`${API}/training-records?${params.toString()}`,
                                  { headers: { Authorization: `Bearer ${token}` } });
      // The endpoint returns a bare array when no page is requested; this call
      // always requests one, so tolerate both shapes rather than assume.
      const data = res.data;
      const items = Array.isArray(data) ? data : (data.items || []);
      setRecords(items);
      setFilteredRecords(items);
      setTotal(Array.isArray(data) ? items.length : (data.total || 0));
      // The server may have moved us to the page holding the located record.
      // Adopt it so the controls do not claim page 1 while showing page 3.
      if (!Array.isArray(data) && data.located_page) {
        locatedRef.current = true;
        setPage(data.located_page);
      } else if (highlightId) {
        // Nothing to locate (record filtered out or gone) — stop asking.
        locatedRef.current = true;
      }
    } catch (err) {
      console.error('Failed to fetch training records:', err);
      showError('Could not load training records.');
    } finally {
      setRecordsLoading(false);
    }
  }, [page, pageSize, filterStatus, searchTerm, sortColumn, sortDirection,
      highlightId, showError]);

  // Records reload whenever anything the SERVER query depends on changes.
  // page/pageSize/filter/sort are already baked into fetchRecords' identity;
  // activeSiteId is not, because the server derives the site scope from the
  // session, so it is listed here to force a reload on a site switch.
  //
  // Placed AFTER fetchRecords: a hook's dependency array is evaluated during
  // render, at the point the useEffect call appears in the source, so an effect
  // written above the `const fetchRecords` declaration reads that binding
  // inside its temporal dead zone and throws before the page can render.
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, activeSiteId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // allSettled: a role without users:read (e.g. Safety Officer) gets a 403
      // on /users; with Promise.all that rejection also threw away the course
      // list, leaving Schedule Training with an empty course dropdown.
      const [coursesRes, usersRes] = await Promise.allSettled([
        axios.get(`${API}/training-courses`, { headers }),
        axios.get(activeSiteId ? `${API}/users?site_id=${activeSiteId}` : `${API}/users`, { headers })
      ]);

      if (coursesRes.status === 'fulfilled') {
        setCourses(coursesRes.value.data);
      } else {
        console.error('Failed to fetch courses:', coursesRes.reason);
      }
      if (usersRes.status === 'fulfilled') {
        setEmployees(usersRes.value.data);
        setFilteredEmployees(usersRes.value.data);
      } else {
        console.error('Failed to fetch users:', usersRes.reason);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openScheduleModal = (record = null) => {
    if (record) {
      setEditingRecord(record);
      setScheduleForm({
        employee_id: record.employee_id,
        // A group session's employee_name is a DERIVED summary
        // ("User1 +2 more"), not a person. Loading it into the form sent it
        // straight back on save, where it was stored as the trainee's name and
        // the label then stopped tracking the attendee list.
        employee_name: (record.attendee_count > 0 && !record.employee_id)
          ? '' : record.employee_name,
        course_id: record.course_id,
        course_title: record.course_title,
        // A group session may carry no due date at all — the window says when
        // it ran — so this can be null. It always could be; a session record
        // is simply the first case that reaches it.
        training_date: record.training_date
          ? String(record.training_date).split('T')[0]
          : (record.training_start
              ? toTenantLocalInput(record.training_start, tenantTimezone).slice(0, 10)
              : ''),
        // Stored as instants; shown as the site's wall clock, which is what
        // the requester entered.
        training_start: record.training_start
          ? toTenantLocalInput(record.training_start, tenantTimezone) : '',
        training_end: record.training_end
          ? toTenantLocalInput(record.training_end, tenantTimezone) : '',
        attendees: Array.isArray(record.attendees) ? record.attendees : [],
        completion_status: record.completion_status,
        trainer_name: record.trainer_name || '',
        duration_hours: record.duration_hours || '',
        notes: record.notes || ''
      });
      // Show the employee name when editing a per-person record. A group
      // session's employee_name is a DERIVED summary ("Ravi Kumar +3 more"),
      // so putting it here would read as a selected employee that is not one.
      setEmployeeSearch(
        (record.attendee_count > 0 && !record.employee_id) ? '' : (record.employee_name || '')
      );
      setSelectedEmployees([]);
    } else {
      setEditingRecord(null);
      setScheduleForm({
        employee_id: '',
        employee_name: '',
        course_id: '',
        course_title: '',
        training_date: new Date().toISOString().split('T')[0],
        training_start: '',
        training_end: '',
        attendees: [],
        completion_status: 'Scheduled',
        trainer_name: '',
        duration_hours: '',
        notes: ''
      });
      // Clear employeeSearch when creating new
      setEmployeeSearch('');
      setSelectedEmployees([]);
    }
    setShowEmployeeDropdown(false);
    // Reset the trainee picker's contractor for BOTH branches: a firm left over
    // from the last scheduling would narrow the worker list to the wrong
    // contractor on the next one.
    setTraineeContractorId('');
    setShowScheduleModal(true);
  };

  const openCompleteModal = async (record) => {
    setEditingRecord(record);
    setCompleteForm({
      quiz_score: record.quiz_score || '',
      trainer_evaluation: record.trainer_evaluation || '',
      manager_evaluation: record.manager_evaluation || '',
      certificate_issued: record.certificate_issued || false,
      certificate_number: record.certificate_number || '',
      attendance_verified: record.attendance_verified || false
    });
    // Find the course to get passing score and quiz availability
    const course = courses.find(c => c.id === record.course_id);
    setCoursePassingScore(course?.passing_score || null);
    setCourseHasQuiz(course?.has_quiz || false);
    setCourseEvidenceRequired(course?.evidence_required || false);

    // Fetch existing attachments for this record
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/training-records/${record.id}/attachments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const files = res.data.attachments || [];
      setUploadedFiles(files);
      setInitialAttachmentIds(files.map(f => f.id));
    } catch (err) {
      console.error('Failed to fetch attachments:', err);
      setUploadedFiles([]);
      setInitialAttachmentIds([]);
    }

    setShowCompleteModal(true);
  };

  const handleScheduleSave = async () => {
    // TRN-07: never submit a window that ends at or before it starts. The
    // button is disabled for this too; this guards the keyboard / stale-state
    // path so the server's 400 is not the first the user hears of it.
    if (scheduleForm.training_start && scheduleForm.training_end
        && sessionHours(scheduleForm.training_start, scheduleForm.training_end) === null) {
      showError('End time must be after the start time');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingRecord) {
        // Edit mode is single-record by definition — no employee swap
        // possible in the UI, so just PUT the existing endpoint.
        const editRes = await axios.put(
          `${API}/training-records/${editingRecord.id}`,
          {
            ...scheduleForm,
            training_date: new Date(scheduleForm.training_date).toISOString(),
            training_start: tenantInputToInstant(scheduleForm.training_start, tenantTimezone)?.toISOString() ?? null,
            training_end: tenantInputToInstant(scheduleForm.training_end, tenantTimezone)?.toISOString() ?? null,
            attendees: scheduleForm.attendees,
            duration_hours: scheduleForm.duration_hours ? parseFloat(scheduleForm.duration_hours) : null,
            site_id: activeSiteId || undefined,
            site_name: activeSiteName || undefined,
          },
          { headers }
        );
        // The server flags a session whose length differs materially from the
        // course's; show it rather than letting it sit in the server log.
        if (editRes.data?.duration_warning) {
          showWarning(editRes.data.duration_warning);
        } else {
          showSuccess('Training schedule updated successfully');
        }
      } else {
        // Create mode — always use the bulk endpoint, even for a single
        // employee, so server-side semantics (atomic insert, single
        // audit row, deterministic notification ordering) are
        // identical regardless of selection size.
        if (selectedEmployees.length === 0 && scheduleForm.attendees.length === 0) {
          showError('Select at least one employee, or add an attendee');
          return;
        }

        // A GROUP SESSION — attendees but nobody selected from the employee
        // picker — is one record listing who came, not one record per person.
        // The bulk endpoint fans out per employee and has nothing to fan out
        // here, so it goes to the single-record endpoint instead.
        if (selectedEmployees.length === 0) {
          const sessionRes = await axios.post(
            `${API}/training-records`,
            {
              course_id: scheduleForm.course_id,
              course_title: scheduleForm.course_title,
              training_date: new Date(scheduleForm.training_date).toISOString(),
              training_start: tenantInputToInstant(scheduleForm.training_start, tenantTimezone)?.toISOString() ?? null,
              training_end: tenantInputToInstant(scheduleForm.training_end, tenantTimezone)?.toISOString() ?? null,
              attendees: scheduleForm.attendees,
              completion_status: scheduleForm.completion_status,
              trainer_name: scheduleForm.trainer_name || undefined,
              duration_hours: scheduleForm.duration_hours ? parseFloat(scheduleForm.duration_hours) : null,
              notes: scheduleForm.notes || undefined,
              site_id: activeSiteId || undefined,
              site_name: activeSiteName || undefined,
            },
            { headers }
          );
          if (sessionRes.data?.duration_warning) {
            showWarning(sessionRes.data.duration_warning);
          } else {
            showSuccess(
              `Session recorded with ${scheduleForm.attendees.length} attendee${
                scheduleForm.attendees.length === 1 ? '' : 's'}`
            );
          }
          setShowScheduleModal(false);
          if (page === 1) fetchRecords(); else setPage(1);
          return;
        }
        const res = await axios.post(
          `${API}/training-records/bulk`,
          {
            // A contractor worker has no login, so employee_id carries the
            // CONTRACTOR WORKER id and contractor_worker_id marks it as such.
            // That is what makes induction-as-training work for contractor
            // personnel: the record points at the contractor master, and
            // eligibility reads it when checking required training.
            employees: selectedEmployees.map(e => ({
              employee_id: e.id,
              employee_name: e.name,
              contractor_worker_id: e.is_contractor_worker ? e.id : undefined,
            })),
            course_id: scheduleForm.course_id,
            course_title: scheduleForm.course_title,
            training_date: new Date(scheduleForm.training_date).toISOString(),
            training_start: tenantInputToInstant(scheduleForm.training_start, tenantTimezone)?.toISOString() ?? null,
            training_end: tenantInputToInstant(scheduleForm.training_end, tenantTimezone)?.toISOString() ?? null,
            attendees: scheduleForm.attendees,
            completion_status: scheduleForm.completion_status,
            trainer_name: scheduleForm.trainer_name || undefined,
            duration_hours: scheduleForm.duration_hours ? parseFloat(scheduleForm.duration_hours) : null,
            notes: scheduleForm.notes || undefined,
            site_id: activeSiteId || undefined,
            site_name: activeSiteName || undefined,
          },
          { headers }
        );
        // TRN-01: employees AND typed-in attendees together produce one record
        // per employee plus one group-session record for the attendees, so
        // created_count is not a head count — report the two separately.
        const n = res.data?.employee_count ?? selectedEmployees.length;
        const extra = res.data?.session_attendee_count || 0;
        const base = n === 1
          ? 'Training scheduled successfully'
          : `Training scheduled for ${n} employees`;
        const msg = extra
          ? `${base}, plus a session record for ${extra} attendee${extra === 1 ? '' : 's'}`
          : base;
        if (res.data?.duration_warning) {
          showWarning(`${msg}. ${res.data.duration_warning}`);
        } else {
          showSuccess(msg);
        }
      }

      setShowScheduleModal(false);
      // fetchData no longer loads records (see fetchRecords), so the list has
      // to be refreshed explicitly or a saved record would not appear.
      // A NEW record lands at the top of the default ordering, so go to page 1;
      // an edit stays where the user was.
      if (editingRecord) fetchRecords();
      else if (page === 1) fetchRecords();
      else setPage(1);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to save training record');
    }
  };

  // Toggle an employee in the multi-select buffer used in create mode.
  // Re-selecting a chosen employee removes them — same affordance as the
  // pill-row × button — so the dropdown behaves consistently either way.
  const handleToggleEmployee = (empId) => {
    const emp = employees.find(e => e.id === empId);
    if (!emp) return;
    setSelectedEmployees(prev => {
      const exists = prev.some(p => p.id === empId);
      if (exists) return prev.filter(p => p.id !== empId);
      return [...prev, { id: emp.id, name: emp.name }];
    });
  };

  const handleRemoveSelectedEmployee = (empId) => {
    setSelectedEmployees(prev => prev.filter(p => p.id !== empId));
  };

  // Add a CONTRACTOR WORKER to the same selection buffer as employees, flagged
  // so the payload can tell the server which id space it is in. Sharing the
  // buffer means the chips, the count and the bulk submit all work unchanged —
  // a contractor worker is just another trainee.
  const handleAddContractorWorker = (contractorId, workerId, selected) => {
    // Remember the firm even when only the firm was picked, so the worker list
    // stays narrowed to it and the choice is visible.
    setTraineeContractorId(contractorId || '');
    if (!workerId) return;
    const worker = selected?.worker;
    setSelectedEmployees(prev => {
      if (prev.some(p => p.id === workerId)) return prev;
      const label = worker?.name
        ? `${worker.name}${worker.contractor_company ? ` (${worker.contractor_company})` : ''}`
        : 'Contractor worker';
      return [...prev, { id: workerId, name: label, is_contractor_worker: true }];
    });
  };

  const handleCompleteSave = async () => {
    if (!editingRecord) return;

    // Validate mandatory fields before submission
    if (courseHasQuiz && (!completeForm.quiz_score || completeForm.quiz_score === '')) {
      showError('Quiz score is required for this course. Please enter the quiz score.');
      return;
    }
    if (courseEvidenceRequired && uploadedFiles.length === 0) {
      showError('Evidence (file attachment) is required for this course. Please upload at least one file.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Determine which files are new vs removed
      const currentFileIds = uploadedFiles.map(f => f.id);
      const newFileIds = currentFileIds.filter(id => !initialAttachmentIds.includes(id));
      const removedFileIds = initialAttachmentIds.filter(id => !currentFileIds.includes(id));

      const data = {
        ...completeForm,
        quiz_score: completeForm.quiz_score ? parseInt(completeForm.quiz_score) : null,
        trainer_evaluation: completeForm.trainer_evaluation ? parseInt(completeForm.trainer_evaluation) : null,
        manager_evaluation: completeForm.manager_evaluation ? parseInt(completeForm.manager_evaluation) : null,
        completion_status: 'Completed',
        file_ids: newFileIds.length > 0 ? newFileIds : undefined
      };

      await axios.put(
        `${API}/training-records/${editingRecord.id}`,
        data,
        { headers }
      );

      // Detach removed files
      for (const fileId of removedFileIds) {
        try {
          await axios.delete(
            `${API}/training-records/${editingRecord.id}/attachments/${fileId}`,
            { headers }
          );
        } catch (err) {
          console.error('Failed to detach file:', err);
        }
      }

      setShowCompleteModal(false);
      // Editing in place — stay on the current page.
      fetchRecords();
      showSuccess('Training record updated successfully');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to update training record');
    }
  };

  const handleEmployeeSelect = (empId) => {
    const employee = employees.find(emp => emp.id === empId);
    if (employee) {
      setScheduleForm({
        ...scheduleForm,
        employee_id: empId,
        employee_name: employee.name
      });
      setEmployeeSearch(employee.name);
      setShowEmployeeDropdown(false);
    }
  };

  const handleClearEmployee = () => {
    setScheduleForm({
      ...scheduleForm,
      employee_id: '',
      employee_name: ''
    });
    setEmployeeSearch('');
    setShowEmployeeDropdown(true);
  };

  const { tenant } = useAuth();
  const tenantTimezone = tenant?.timezone || 'UTC';

  // Hours between the two wall clocks, in the SITE's zone. Returns null when
  // the window is incomplete or impossible, so callers can tell "not set yet"
  // from "wrong".
  const sessionHours = (startLocal, endLocal) => {
    if (!startLocal || !endLocal) return null;
    const a = tenantInputToInstant(startLocal, tenantTimezone);
    const b = tenantInputToInstant(endLocal, tenantTimezone);
    if (!a || !b || b <= a) return null;
    return (b - a) / 3600000;
  };

  // The schedule form's window, derived once per render.
  // windowComplete: both ends set, so the duration is CALCULATED from them
  // (TRN-03) and is not separately editable. windowInvalid: both set but the
  // end is not after the start — Save stays disabled (TRN-07).
  const scheduleWindowComplete = !!(scheduleForm.training_start && scheduleForm.training_end);
  const scheduleWindowHours = scheduleWindowComplete
    ? sessionHours(scheduleForm.training_start, scheduleForm.training_end) : null;
  const scheduleWindowInvalid = scheduleWindowComplete && scheduleWindowHours === null;
  // The course's NOMINAL duration, for the variance hint. Read from the course
  // itself rather than the form's duration field: in edit mode that field holds
  // the record's actual hours, which would compare the session with itself.
  const selectedCourseNominalHours = (() => {
    const c = courses.find((x) => x.id === scheduleForm.course_id);
    return Number(c ? c.duration_hours : scheduleForm.duration_hours) || 0;
  })();

  // --- Attendees -------------------------------------------------------
  // Same idea as a permit's workers: an attendee is a system employee, a
  // contractor worker, or a NAME TYPED IN for someone not in the system yet.
  // Induction for new joiners is the case this exists for.
  const [attendeeName, setAttendeeName] = useState('');
  // Suggestions from the HRMS employee master — the internal counterpart of
  // the contractor worker registry. Most inductees are already here: they
  // joined and appear in the master long before anyone makes them a user.
  const [attendeeMatches, setAttendeeMatches] = useState([]);

  useEffect(() => {
    const term = attendeeName.trim();
    if (term.length < 2 || !activeSiteId) { setAttendeeMatches([]); return; }
    let cancelled = false;
    // Debounced: this fires on every keystroke of a name being typed.
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/hrms/employees`, {
          params: { site_id: activeSiteId, search: term, limit: 8 },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAttendeeMatches(res.data?.data || []);
      } catch {
        // The employee master is optional — a tenant without HRMS simply
        // types names in. A failed lookup must not block that.
        if (!cancelled) setAttendeeMatches([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [attendeeName, activeSiteId]);

  const addAttendee = (person = null) => {
    const name = (person ? person.name : attendeeName || '').trim();
    if (!name) return;

    const duplicate = scheduleForm.attendees.some(a =>
      (person?.id && (a.employee_id === person.id
                      || a.employee_master_id === person.id
                      || a.contractor_worker_id === person.id))
      || (a.employee_name || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      showWarning(`${name} is already on the attendee list.`);
      setAttendeeName('');
      return;
    }

    setScheduleForm(prev => ({
      ...prev,
      attendees: [...prev.attendees, {
        id: Math.random().toString(36).substr(2, 9),
        // No id behind the name means the person is not in the system — which
        // is exactly the induction case, not an error.
        // employee_id is a system USER id; a person from the employee master
        // has no login, so they are referenced by employee_master_id — the
        // same way a contractor worker is referenced by contractor_worker_id.
        employee_id: '',
        employee_master_id: person && !person.is_contractor_worker ? person.id : null,
        employee_code: person?.employee_code || null,
        department: person?.department || null,
        employee_name: name,
        is_manual: !person,
        contractor_worker_id: person?.is_contractor_worker ? person.id : null,
        contractor_company: person?.contractor_company || null,
        attended: true,
      }],
    }));
    setAttendeeName('');
  };

  const removeAttendee = (id) =>
    setScheduleForm(prev => ({
      ...prev, attendees: prev.attendees.filter(a => a.id !== id),
    }));

  const handleCourseSelect = (e) => {
    const courseId = e.target.value;
    const course = courses.find(c => c.id === courseId);
    if (course) {
      setScheduleForm(prev => ({
        ...prev,
        course_id: courseId,
        course_title: course.course_title,
        duration_hours: course.duration_hours || '',
        trainer_name: prev.trainer_name || course.trainer_institution || '',
        // Pre-fill the window from the course's nominal duration; the
        // requester adjusts it to what actually happened.
        training_start: prev.training_start || nowInTenantInput(tenantTimezone),
        training_end: course.duration_hours
          ? addHoursInTenantInput(prev.training_start || nowInTenantInput(tenantTimezone),
                                  Number(course.duration_hours), tenantTimezone)
          : prev.training_end,
      }));
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      case 'Scheduled': return 'bg-gray-100 text-gray-700';
      case 'Failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/training')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Training Dashboard
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">📋 Training Records</h1>
              <p className="text-gray-600 mt-1">
                {total} record(s) found
                {totalPages > 1 && (
                  <span className="text-gray-400">
                    {' '}· showing {(page - 1) * pageSize + 1}–
                    {Math.min(page * pageSize, total)}
                  </span>
                )}
              </p>
            </div>
            {hasPermission('training', 'create') && (
              <button
                onClick={() => openScheduleModal()}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Schedule Training
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex gap-2">
            {['All', 'Scheduled', 'In Progress', 'Completed', 'Failed'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  filterStatus === status
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status}
              </button>
            ))}
          </div>

          {/* Search and export. Both run against the SERVER with the same
              filters, so what you export is what you are looking at. */}
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <div className="relative flex-1 min-w-[240px]">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search employee, attendee, course, trainer, certificate or notes"
                className="w-full px-4 py-2 pr-9 border border-gray-300 rounded-lg
                           focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  title="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => exportRecords('xlsx')}
              disabled={exporting !== null || total === 0}
              className="shrink-0 px-4 py-2 bg-green-700 text-white rounded-lg
                         hover:bg-green-800 disabled:opacity-50"
            >
              {exporting === 'xlsx' ? 'Preparing…' : 'Export Excel'}
            </button>
            <button
              type="button"
              onClick={() => exportRecords('pdf')}
              disabled={exporting !== null || total === 0}
              className="shrink-0 px-4 py-2 bg-gray-700 text-white rounded-lg
                         hover:bg-gray-800 disabled:opacity-50"
            >
              {exporting === 'pdf' ? 'Preparing…' : 'Export PDF'}
            </button>
          </div>

          {/* Say what the export will contain — the count is the filtered
              total, not the page. */}
          {(searchTerm || filterStatus !== 'All') && (
            <p className="text-sm text-gray-500 mt-2">
              {total} record{total === 1 ? '' : 's'} match
              {searchTerm ? ` "${searchTerm}"` : ''}
              {filterStatus !== 'All' ? ` with status ${filterStatus}` : ''}
              {' '}— an export covers all of them, not just this page.
            </p>
          )}
        </div>

        {/* Records Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('employee_name')}
                  >
                    <div className="flex items-center gap-1">
                      Employee
                      {getSortIcon('employee_name')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('course_title')}
                  >
                    <div className="flex items-center gap-1">
                      Course / Training Type
                      {getSortIcon('course_title')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Certificate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRecords.map(record => (
                  <tr
                    key={record.id}
                    ref={record.id === highlightId ? highlightedRecordRef : null}
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                      record.id === highlightId ? 'bg-yellow-100 ring-2 ring-yellow-400' : ''
                    }`}
                    onClick={() => navigate(`/training/records/${record.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{record.employee_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{record.course_title}</div>
                      {record.trainer_name && (
                        <div className="text-xs text-gray-500">Trainer: {record.trainer_name}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(record.training_date)}
                        {/* The session window, in the site's wall clock, shown
                            under the date when it was recorded. */}
                        {/* A group session shows its headcount rather than a
                            single trainee name. */}
                        {record.attendee_count > 0 && (
                          <div className="text-xs text-gray-500">
                            {record.attendee_count} attendee{record.attendee_count === 1 ? '' : 's'}
                          </div>
                        )}
                        {record.training_start && record.training_end && (
                          <div className="text-xs text-gray-500">
                            {toTenantLocalInput(record.training_start, tenantTimezone).slice(11)}
                            {' – '}
                            {toTenantLocalInput(record.training_end, tenantTimezone).slice(11)}
                            {record.duration_hours ? ` · ${record.duration_hours} h` : ''}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          getStatusColor(record.completion_status)
                        }`}>
                          {record.completion_status}
                        </span>
                        {record.completion_status === 'Scheduled' && record.training_date && new Date(record.training_date) < new Date(new Date().toDateString()) && (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-700">
                            Overdue
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.quiz_score ? `${record.quiz_score}%` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record.certificate_issued ? (
                        <div className="flex items-center gap-2">
                          <span className="text-green-600">✓ Issued</span>
                          {record.completion_status === 'Completed' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openCompleteModal(record); }}
                              className="text-blue-500 hover:text-blue-700"
                              title="View/manage attachments"
                            >
                              📎
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {hasPermission('training', 'update') && record.completion_status !== 'Completed' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openScheduleModal(record); }}
                          className="text-blue-600 hover:underline mr-3"
                        >
                          Edit Schedule
                        </button>
                      )}
                      {hasPermission('training', 'update') && (
                        record.completion_status === 'Completed' ? (
                          isAdmin() && courses.find(c => c.id === record.course_id)?.has_quiz && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openCompleteModal(record); }}
                              className="text-purple-600 hover:underline"
                            >
                              Edit Scores
                            </button>
                          )
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); openCompleteModal(record); }}
                            className="text-green-600 hover:underline"
                          >
                            Complete
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredRecords.length === 0 && !recordsLoading && (
            <div className="text-center py-12">
              <p className="text-gray-600">
                {total > 0
                  ? 'No records on this page.'
                  : 'No training records found'}
              </p>
              {total > 0 && (
                <button
                  onClick={() => setPage(1)}
                  className="mt-2 text-sm font-medium text-blue-600 hover:underline"
                >
                  Back to the first page
                </button>
              )}
            </div>
          )}

          {/* Pagination. Rendered whenever there is more than one page, so a
              short list stays as uncluttered as it was before. */}
          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3
                            border-t border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <label htmlFor="tr-page-size">Rows per page</label>
                <select
                  id="tr-page-size"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="rounded-md border border-gray-300 px-2 py-1 text-sm
                             focus:border-blue-500 focus:outline-none
                             focus:ring-1 focus:ring-blue-500"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || recordsLoading}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm
                             font-medium text-gray-700 hover:bg-gray-50
                             disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-1 text-sm text-gray-600 tabular-nums">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || recordsLoading}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm
                             font-medium text-gray-700 hover:bg-gray-50
                             disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Schedule Training Modal */}
        {showScheduleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingRecord ? 'Update Training Schedule' : 'Schedule Training'}
                </h2>
                <button onClick={() => setShowScheduleModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Employee Selection with Search.
                      Edit mode: read-only display of the original assignee
                                 (you can't reassign by editing — delete + reschedule).
                      Create mode: multi-select via checkbox dropdown +
                                   pill row of selected employees + count badge. */}
                  <div className="relative" ref={dropdownRef}>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {editingRecord ? 'Employee' : 'Employees'} <span className="text-red-500">*</span>
                      {!editingRecord && selectedEmployees.length > 0 && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                          {selectedEmployees.length} selected
                        </span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                        onFocus={() => !editingRecord && setShowEmployeeDropdown(true)}
                        disabled={!!editingRecord}
                        placeholder={editingRecord ? '' : 'Search by name, email, or ID — pick multiple'}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                      />
                    </div>
                    {showEmployeeDropdown && !editingRecord && filteredEmployees.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredEmployees.slice(0, 50).map(emp => {
                          const isChecked = selectedEmployees.some(s => s.id === emp.id);
                          return (
                            <label
                              key={emp.id}
                              className={`w-full flex items-center justify-between gap-2 px-4 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${isChecked ? 'bg-blue-50' : 'hover:bg-blue-50'}`}
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleToggleEmployee(emp.id)}
                                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 shrink-0"
                                />
                                <div className="min-w-0">
                                  <div className="font-semibold text-gray-800 truncate">{emp.name || 'Unknown'}</div>
                                  <div className="text-xs text-gray-500 truncate">{emp.email || emp.id || 'N/A'}</div>
                                </div>
                              </div>
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded shrink-0">{emp.role || 'User'}</span>
                            </label>
                          );
                        })}
                        {filteredEmployees.length > 50 && (
                          <div className="px-4 py-2 text-xs text-gray-500 text-center bg-gray-50">
                            Showing first 50 results. Type to refine search.
                          </div>
                        )}
                      </div>
                    )}
                    {/* Edit-mode confirmation strip */}
                    {editingRecord && scheduleForm.employee_name && (
                      <p className="text-xs text-green-600 mt-1">✓ {scheduleForm.employee_name}</p>
                    )}
                    {/* Create-mode selected pills */}
                    {!editingRecord && selectedEmployees.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {selectedEmployees.map(emp => (
                          <span
                            key={emp.id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                          >
                            {emp.name}
                            <button
                              type="button"
                              onClick={() => handleRemoveSelectedEmployee(emp.id)}
                              className="text-blue-500 hover:text-blue-800"
                              aria-label={`Remove ${emp.name}`}
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Contractor workers as trainees. They have no login, so
                      they cannot appear in the employee list above — this is how
                      a contractor worker gets assigned an induction or any other
                      required course. Selecting one adds them to the same
                      selection buffer, so the count, the chips and the bulk
                      submit all work unchanged. Create mode only: an existing
                      record's trainee is not reassignable. */}
                  {!editingRecord && (
                    <ContractorPicker
                      contractorId={traineeContractorId}
                      workerId=""
                      siteId={activeSiteId}
                      alwaysOpen={false}
                      onChange={handleAddContractorWorker}
                      hint="Assign this training to a contractor worker — how induction and required training are recorded for contractor personnel."
                    />
                  )}

                  {/* Course Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Training Course <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={scheduleForm.course_id}
                      onChange={handleCourseSelect}
                      disabled={!!editingRecord}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                    >
                      <option value="">Select Course</option>
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.course_title} ({course.training_type})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Training Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Training Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={scheduleForm.training_date}
                      onChange={(e) =>
                        setScheduleForm({ ...scheduleForm, training_date: e.target.value })
                      }
                      min={new Date().toISOString().split("T")[0]}
                      max="2099-12-31"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Session window. Times are the SITE's wall clock, not the
                      viewer's — a session is an event at the site. */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start date &amp; time
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleForm.training_start}
                      onChange={(e) => setScheduleForm(prev => {
                        const start = toWallClockInput(e.target.value);
                        return {
                          ...prev,
                          training_start: start,
                          // Carry the window along while the end still matches
                          // the course's nominal length — moving the start
                          // otherwise leaves the end behind it.
                          training_end:
                            (!prev.training_end && prev.duration_hours)
                              ? addHoursInTenantInput(start, Number(prev.duration_hours), tenantTimezone)
                              : prev.training_end,
                        };
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      End date &amp; time
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleForm.training_end}
                      onChange={(e) => setScheduleForm({
                        ...scheduleForm, training_end: toWallClockInput(e.target.value),
                      })}
                      min={scheduleForm.training_start || undefined}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        scheduleForm.training_start && scheduleForm.training_end
                        && !sessionHours(scheduleForm.training_start, scheduleForm.training_end)
                          ? 'border-red-400' : 'border-gray-300'
                      }`}
                    />
                    {/* Say what the entered window actually means, and flag a
                        variance against the course rather than blocking it —
                        a session that overran must still be recordable. */}
                    {scheduleForm.training_start && scheduleForm.training_end && (() => {
                      const hrs = sessionHours(scheduleForm.training_start, scheduleForm.training_end);
                      if (hrs === null) {
                        return <p className="text-red-500 text-sm mt-1">End must be after the start</p>;
                      }
                      const nominal = selectedCourseNominalHours;
                      const off = nominal > 0 && (hrs < nominal * 0.75 || hrs > nominal * 1.25);
                      return (
                        <p className={`text-sm mt-1 ${off ? 'text-amber-600' : 'text-gray-500'}`}>
                          Duration {hrs.toFixed(2)} h
                          {nominal > 0 && ` · course is ${nominal} h`}
                          {off && ' — check the times'}
                        </p>
                      );
                    })()}
                  </div>

                  {/* Attendees — for a group session such as induction.
                      A name with no employee behind it is the point: new
                      joiners attend before anyone has created them as users. */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Attendees
                      {scheduleForm.attendees.length > 0 && (
                        <span className="ml-2 text-gray-500 font-normal">
                          {scheduleForm.attendees.length} added
                        </span>
                      )}
                    </label>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={attendeeName}
                        onChange={(e) => setAttendeeName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); addAttendee(); }
                        }}
                        placeholder="Type a name and press Enter — for someone not in the system yet"
                        className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => addAttendee()}
                        disabled={!attendeeName.trim()}
                        className="shrink-0 px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
                      >
                        Add
                      </button>
                    </div>
                    {/* Matches from the employee master. Picking one records
                        the person's code and department; typing a name that
                        matches nobody is still fine — that is the new joiner
                        who has not reached the master yet. */}
                    {attendeeMatches.length > 0 && (
                      <ul className="mt-1 border border-gray-200 rounded-lg divide-y max-h-44 overflow-y-auto">
                        {attendeeMatches.map(emp => (
                          <li key={emp.id}>
                            <button
                              type="button"
                              onClick={() => addAttendee(emp)}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50"
                            >
                              <span className="font-medium">{emp.name}</span>
                              <span className="text-xs text-gray-500 ml-2">
                                {[emp.employee_code, emp.department, emp.designation]
                                  .filter(Boolean).join(' · ')}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    <p className="text-xs text-gray-500 mt-1">
                      Use this for induction and other group sessions. Start
                      typing to find an employee, or enter a name that is not on
                      the system yet — a new joiner attending on day one.
                    </p>

                    {scheduleForm.attendees.length > 0 && (
                      <ul className="mt-3 border border-gray-200 rounded-lg divide-y">
                        {scheduleForm.attendees.map(a => (
                          <li key={a.id} className="flex items-center justify-between px-3 py-2">
                            <span className="min-w-0 flex items-center gap-2">
                              <span className="truncate">{a.employee_name}</span>
                              {a.contractor_worker_id ? (
                                <span className="shrink-0 px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700">
                                  Contractor{a.contractor_company ? ` · ${a.contractor_company}` : ''}
                                </span>
                              ) : a.employee_master_id ? (
                                <span className="shrink-0 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                                  Employee{a.employee_code ? ` · ${a.employee_code}` : ''}
                                </span>
                              ) : a.is_manual ? (
                                <span className="shrink-0 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                                  Not on record
                                </span>
                              ) : (
                                <span className="shrink-0 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                                  Employee
                                </span>
                              )}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeAttendee(a.id)}
                              className="shrink-0 text-red-600 text-sm hover:underline"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={scheduleForm.completion_status}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, completion_status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Scheduled">Scheduled</option>
                      <option value="In Progress">In Progress</option>
                    </select>
                  </div>

                  {/* Trainer Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Trainer Name</label>
                    <input
                      type="text"
                      value={scheduleForm.trainer_name}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, trainer_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Duration (hours)</label>
                    {/* TRN-03: with both start and end set the server DERIVES
                        the duration from the window, so a typed value here was
                        silently discarded. Show the calculated figure read-only;
                        the field is editable only while the window is open. */}
                    <input
                      type="number"
                      step="0.5"
                      value={scheduleWindowComplete
                        ? (scheduleWindowHours !== null ? scheduleWindowHours.toFixed(2) : '')
                        : scheduleForm.duration_hours}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, duration_hours: e.target.value })}
                      disabled={scheduleWindowComplete}
                      readOnly={scheduleWindowComplete}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-600"
                    />
                    {scheduleWindowComplete && (
                      <p className="text-xs text-gray-500 mt-1">Calculated from start and end time</p>
                    )}
                  </div>

                  {/* Notes */}
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={scheduleForm.notes}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
                      rows={3}
                      placeholder="Any special instructions or requirements..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleScheduleSave}
                  disabled={
                    !scheduleForm.course_id ||
                    // TRN-07: an end at or before the start is never saveable.
                    scheduleWindowInvalid ||
                    // Mirror the server rule: a record needs an employee OR at
                    // least one attendee. Requiring employee_id alone left a
                    // group session unsaveable, because it never has one.
                    (editingRecord
                      ? (!scheduleForm.employee_id && scheduleForm.attendees.length === 0)
                      : (selectedEmployees.length === 0 && scheduleForm.attendees.length === 0))
                  }
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                >
                  {editingRecord
                    ? 'Update Schedule'
                    : selectedEmployees.length > 1
                      ? `Schedule Training (${selectedEmployees.length})`
                      : 'Schedule Training'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Complete Training Modal */}
        {showCompleteModal && editingRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {editingRecord.completion_status === 'Completed' ? 'Edit Training Scores' : 'Complete Training'}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {editingRecord.employee_name} - {editingRecord.course_title}
                  </p>
                </div>
                <button onClick={() => setShowCompleteModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                  <p className="text-sm text-blue-700">
                    <strong>Training Date:</strong> {formatDate(editingRecord.training_date)}
                  </p>
                  {editingRecord.trainer_name && (
                    <p className="text-sm text-blue-700">
                      <strong>Trainer:</strong> {editingRecord.trainer_name}
                    </p>
                  )}
                </div>

                {/* Warning when score is below passing */}
                {courseHasQuiz && coursePassingScore !== null && completeForm.quiz_score !== '' && parseInt(completeForm.quiz_score) < coursePassingScore && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                    <p className="text-sm text-red-700">
                      <strong>Warning:</strong> The quiz score ({completeForm.quiz_score}%) is below the passing score ({coursePassingScore}%).
                      This training record will be marked as <strong>"Failed"</strong>.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {/* Quiz Score */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Quiz Score (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={courseHasQuiz ? completeForm.quiz_score : ''}
                      onChange={(e) => setCompleteForm({ ...completeForm, quiz_score: e.target.value })}
                      disabled={!courseHasQuiz}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${!courseHasQuiz ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                      placeholder={!courseHasQuiz ? 'N/A - No quiz for this course' : ''}
                    />
                    {!courseHasQuiz && (
                      <p className="text-xs text-gray-500 mt-1">This course does not have a quiz component</p>
                    )}
                    {courseHasQuiz && coursePassingScore !== null && (
                      <div className="mt-1">
                        <span className="text-xs text-gray-500">Pass Mark: {coursePassingScore}%</span>
                        {completeForm.quiz_score !== '' && (
                          <span className={`ml-2 text-xs font-semibold ${
                            parseInt(completeForm.quiz_score) >= coursePassingScore
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}>
                            {parseInt(completeForm.quiz_score) >= coursePassingScore ? '✓ Pass' : '✗ Fail'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Trainer Evaluation */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Trainer Evaluation (1-5)
                    </label>
                    <select
                      value={completeForm.trainer_evaluation}
                      onChange={(e) => setCompleteForm({ ...completeForm, trainer_evaluation: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Not Rated</option>
                      <option value="1">1 - Poor</option>
                      <option value="2">2 - Below Average</option>
                      <option value="3">3 - Average</option>
                      <option value="4">4 - Good</option>
                      <option value="5">5 - Excellent</option>
                    </select>
                  </div>

                  {/* Manager Evaluation */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Manager Evaluation (1-5)
                    </label>
                    <select
                      value={completeForm.manager_evaluation}
                      onChange={(e) => setCompleteForm({ ...completeForm, manager_evaluation: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Not Rated</option>
                      <option value="1">1 - Poor</option>
                      <option value="2">2 - Below Average</option>
                      <option value="3">3 - Average</option>
                      <option value="4">4 - Good</option>
                      <option value="5">5 - Excellent</option>
                    </select>
                  </div>

                  {/* Certificate Number */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Certificate Number</label>
                    <input
                      type="text"
                      value={completeForm.certificate_number}
                      onChange={(e) => setCompleteForm({ ...completeForm, certificate_number: e.target.value })}
                      placeholder="e.g., CERT-2024-001"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Checkboxes */}
                  <div className="col-span-2 space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={completeForm.attendance_verified}
                        onChange={(e) => setCompleteForm({ ...completeForm, attendance_verified: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-gray-700">Attendance Verified</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={completeForm.certificate_issued}
                        onChange={(e) => setCompleteForm({ ...completeForm, certificate_issued: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-gray-700">Certificate Issued</span>
                    </label>

                    {completeForm.certificate_issued && completeForm.certificate_number && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-700">
                          <strong>ℹ️ Note:</strong> A certification will be automatically added to the Certifications log with a 1-year validity period.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Certificate File Upload */}
                  <div className="col-span-2">
                    <FileUploadWidget
                      module="training"
                      referenceId={editingRecord?.id}
                      maxFiles={5}
                      maxSizeMB={10}
                      allowedExtensions={['pdf', 'png', 'jpg', 'jpeg']}
                      existingFiles={uploadedFiles}
                      onFilesChange={setUploadedFiles}
                    />
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowCompleteModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCompleteSave}
                  className={`px-6 py-2 text-white rounded-lg ${
                    editingRecord?.completion_status === 'Completed'
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : coursePassingScore !== null && completeForm.quiz_score !== '' && parseInt(completeForm.quiz_score) < coursePassingScore
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {editingRecord?.completion_status === 'Completed'
                    ? 'Update Scores'
                    : coursePassingScore !== null && completeForm.quiz_score !== '' && parseInt(completeForm.quiz_score) < coursePassingScore
                      ? 'Mark as Failed'
                      : 'Mark as Completed'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default TrainingRecords;